import { useQuery } from '@tanstack/react-query'
import { createRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Inbox,
  Send,
  XCircle,
} from 'lucide-react'
import { MobileBackTitle } from '../components/shell/mobile-back-title'
import { MobileTopCenter } from '../components/shell/mobile-shell-outlets'
import { PageHeader } from '../components/ui/page-header'
import { api, type DocSignature, type DocSignatureStatus, type DocumentSummary } from '../lib/api'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$id/dossier',
  component: UserDossierPage,
})

const STATUS_LABEL: Record<DocSignatureStatus, string> = {
  pending: 'Ausstehend',
  signed: 'Unterschrieben',
  rejected: 'Abgelehnt',
}

function StatusIcon({ status }: { status: DocSignatureStatus }) {
  if (status === 'signed') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />
  if (status === 'rejected') return <XCircle className="h-4 w-4 text-red-500" />
  return <Clock className="h-4 w-4 text-text-tertiary" />
}

function fmtDate(s: string | null) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('de-DE')
}

function UserDossierPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  // Fetch user list to get user details (name/email). Admins can hit /api/users;
  // regular users may not have access — fall back gracefully.
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: !!meData?.user && meData.user.role === 'admin',
  })
  const target = usersData?.users.find((u) => u.id === id) ?? null
  const isSelf = meData?.user?.id === id

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-dossier', id],
    queryFn: () => api.listDocuments({ userId: id }),
    enabled: !!meData?.user,
    retry: false,
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  if (error) {
    const status = (error as Error & { status?: number }).status
    return (
      <div className="space-y-4 max-w-2xl">
        <Link
          to="/settings/users"
          className="inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-accent transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> zurück
        </Link>
        <div className="rounded-2xl border border-border-default bg-bg-elevated/40 px-6 py-12 text-center">
          <XCircle className="mx-auto h-8 w-8 text-text-tertiary" />
          <p className="mt-3 text-text-primary font-medium">
            {status === 403 ? 'Kein Zugriff auf diese Akte' : 'Akte nicht verfügbar'}
          </p>
          <p className="mt-1 text-sm text-text-tertiary">
            {status === 403
              ? 'Du musst Admin oder Team-Leiter dieses Mitarbeiters sein.'
              : (error as Error).message}
          </p>
        </div>
      </div>
    )
  }

  const { documents = [], signatures = [] } = data ?? {}
  const sigsByDoc = new Map<string, DocSignature[]>()
  for (const s of signatures) {
    const list = sigsByDoc.get(s.documentId) ?? []
    list.push(s)
    sigsByDoc.set(s.documentId, list)
  }

  const incoming = documents.filter((d) =>
    signatures.some((s) => s.documentId === d.id && s.recipientUserId === id),
  )
  const outgoing = documents.filter((d) => d.uploaderUserId === id)

  const userLabel = target
    ? `${target.name}${isSelf ? ' · du' : ''}`
    : isSelf
      ? `${meData.user.name} · du`
      : 'Mitarbeiter'
  const userEmail = target?.email ?? (isSelf ? meData.user.email : null)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Mobile-Shell (<640): ←+Name in der Top-Mitte, Minimal-Bar unten
          (Mapping §3). Der ← nimmt dasselbe Ziel wie der In-Content-
          Zurück-Link. */}
      <MobileTopCenter>
        <MobileBackTitle
          title={userLabel}
          onBack={() => navigate({ to: meData.user.role === 'admin' ? '/settings/users' : '/' })}
          backAriaLabel="Zurück zur Mitarbeiter-Liste"
        />
      </MobileTopCenter>

      {/* Einreihiger Standard-Kopf statt Serif-Display (Ticket „obere Leiste
          einheitlich"): Zurück-Link + Avatar + Name + E-Mail in EINER Zeile.
          <640 übernimmt die Shell-Top-Leiste Titel/Eyebrow und der ←-Slot die
          Zurück-Navigation (phone:hidden am Link); Avatar/E-Mail bleiben
          auch mobil. */}
      <PageHeader
        title={userLabel}
        eyebrow="Mitarbeiterakte"
        phoneMode="keep-row"
        className="border-b-0 pb-0"
        inline={
          <>
            <Link
              to={meData.user.role === 'admin' ? '/settings/users' : '/'}
              className="phone:hidden order-first inline-flex items-center gap-1.5 text-sm text-text-tertiary hover:text-accent transition-colors shrink-0"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> zurück
            </Link>
            <div className="order-first flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent text-sm font-semibold">
              {(target?.name ?? meData.user.name).slice(0, 2).toUpperCase()}
            </div>
            {userEmail && (
              <p className="text-sm text-text-secondary min-w-0 truncate">{userEmail}</p>
            )}
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Empfangen" value={incoming.length} />
        <Stat
          label="Ausstehend"
          value={
            signatures.filter((s) => s.recipientUserId === id && s.status === 'pending').length
          }
        />
        <Stat
          label="Unterschrieben"
          value={signatures.filter((s) => s.recipientUserId === id && s.status === 'signed').length}
        />
        <Stat label="Hochgeladen" value={outgoing.length} />
      </div>

      {/* Incoming documents */}
      <Section title="Empfangene Dokumente" icon={<Inbox className="h-4 w-4" />}>
        {isLoading ? (
          <Loading />
        ) : incoming.length === 0 ? (
          <Empty msg="Keine an diesen Mitarbeiter gesendeten Dokumente." />
        ) : (
          <DocList docs={incoming} sigsByDoc={sigsByDoc} mode="incoming" targetUserId={id} />
        )}
      </Section>

      {/* Outgoing documents */}
      <Section title="Versendete / Hochgeladene Dokumente" icon={<Send className="h-4 w-4" />}>
        {isLoading ? (
          <Loading />
        ) : outgoing.length === 0 ? (
          <Empty msg="Dieser Mitarbeiter hat noch nichts hochgeladen." />
        ) : (
          <DocList docs={outgoing} sigsByDoc={sigsByDoc} mode="outgoing" targetUserId={id} />
        )}
      </Section>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-bg-elevated/40 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wider text-text-tertiary">{label}</p>
      <p className="mt-1 font-serif text-2xl text-text-primary tabular-nums">{value}</p>
    </div>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border-subtle bg-bg-overlay/30 text-sm font-medium text-text-secondary">
        {icon}
        {title}
      </header>
      {children}
    </section>
  )
}

function Loading() {
  return <div className="px-4 py-8 text-sm text-text-tertiary text-center">Lade …</div>
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <FileText className="mx-auto h-6 w-6 text-text-tertiary" />
      <p className="mt-2 text-sm text-text-tertiary">{msg}</p>
    </div>
  )
}

function DocList({
  docs,
  sigsByDoc,
  mode,
  targetUserId,
}: {
  docs: DocumentSummary[]
  sigsByDoc: Map<string, DocSignature[]>
  mode: 'incoming' | 'outgoing'
  targetUserId: string
}) {
  return (
    <ul className="divide-y divide-border-subtle">
      {docs.map((d) => {
        const sigs = sigsByDoc.get(d.id) ?? []
        // For incoming view, only show the signature that belongs to the target user
        const relevantSigs =
          mode === 'incoming' ? sigs.filter((s) => s.recipientUserId === targetUserId) : sigs
        return (
          <li key={d.id} className="px-4 py-3 hover:bg-bg-overlay/30 transition-colors">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <Link to="/documents/$id" params={{ id: d.id }} className="min-w-0 flex-1 group">
                <p className="text-sm font-medium text-text-primary truncate group-hover:text-accent transition-colors">
                  {d.filename}
                </p>
                <p className="mt-0.5 text-xs text-text-tertiary tabular-nums">
                  {fmtDate(d.createdAt)} · {(d.sizeBytes / 1024).toFixed(0)} KB
                  {d.pageCount ? ` · ${d.pageCount} Seiten` : ''}
                </p>
              </Link>
              <a
                href={api.documentFileUrl(d.id)}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-text-tertiary hover:text-accent inline-flex items-center gap-1"
              >
                <Download className="h-3.5 w-3.5" />
                Original
              </a>
            </div>
            {relevantSigs.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {relevantSigs.map((s) => (
                  <SignaturePill key={s.id} sig={s} docId={d.id} />
                ))}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function SignaturePill({ sig, docId }: { sig: DocSignature; docId: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-overlay/40 pl-2 pr-1 py-0.5 text-xs">
      <StatusIcon status={sig.status} />
      <span className="text-text-secondary">{STATUS_LABEL[sig.status]}</span>
      {sig.signedAt && (
        <span className="text-text-tertiary tabular-nums">{fmtDate(sig.signedAt)}</span>
      )}
      {sig.status === 'signed' && sig.signedStoragePath && (
        <a
          href={api.documentSignedFileUrl(docId, sig.id)}
          target="_blank"
          rel="noreferrer"
          aria-label="Unterzeichnete Version herunterladen"
          className="ml-1 rounded-full p-1 text-accent hover:bg-accent/10 transition-colors"
        >
          <Download className="h-3 w-3" />
        </a>
      )}
    </div>
  )
}
